import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import has from "lodash/fp/has";
import head from "lodash/fp/head";
import isNil from "lodash/fp/isNil";
import merge from "lodash/fp/merge";
import omit from "lodash/fp/omit";
import omitBy from "lodash/fp/omitBy";
import split from "lodash/fp/split";

import getSkateCustomiseBladeValuesFromRecipe from "./getSkateCustomiseBladeValuesFromRecipe";

export default function transformRecipeData({
  initialUserChoiceValues = {},
  recipeData = {},
  productFamily,
  productCustomisations,
}) {
  if (recipeData?.webAppValues) {
    return flow(
      getOr({}, "webAppValues"),
      JSON.parse,
      omitBy(isNil),
      merge(initialUserChoiceValues)
    )(recipeData);
  }
  //DEV NOTE: Will run through swith case if `RecipeData` does not contain raw userChoiceValues under `webAppValues`

  switch (productFamily) {
    case "HyperliteGoalieStick2023":
    case "VaporGoalieStick2025":
    case "QuickTurnVaporFlyliteGoalieStick":
      return flow(
        omit(["Personalization (player name)"]),
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )({
        ...recipeData,
        playerName: recipeData["Personalization (player name)"],
        PentaGrip: recipeData["PentaGrip"] === "Remove PentaGrip",
        noPersonalization: has("Personalization (player name)")(recipeData)
          ? false
          : has("noPersonalization")(initialUserChoiceValues)
          ? initialUserChoiceValues.noPersonalization
          : true,
      });

    case "MachGoalieStick2022":
    case "ShadowGoalieStick2024":
    case "QuickTurnSupremeShadowGoalieStick":
      const shaftColorCode =
        flow(get("Shaft Color code"), split("_"), head)(recipeData) ||
        initialUserChoiceValues["Shaft Color code"];

      return flow(
        omit(["Name Bar ('Nick Name')", "Shaft Color text"]),
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )({
        ...recipeData,
        playerName: recipeData["Name Bar ('Nick Name')"],
        PentaGrip: recipeData["PentaGrip"] === "Add PentaGrip",
        "Shaft Color code": shaftColorCode,
        noPersonalization: has("Name Bar ('Nick Name')")(recipeData)
          ? false
          : has("noPersonalization")(initialUserChoiceValues)
          ? initialUserChoiceValues.noPersonalization
          : true,
      });

    case "Hyperlite2Skate2023":
    case "SupremeMachSkate2022":
      const skateCustomiseBlade =
        recipeData["Blade Name"] ||
        recipeData["Blade Number"] ||
        recipeData["Performance Radius"] ||
        recipeData["Sharpening"]
          ? true
          : false;

      const customiseBladeValues = skateCustomiseBlade
        ? getSkateCustomiseBladeValuesFromRecipe(recipeData)(
            productCustomisations
          )
        : {};

      return flow(
        omit([]),
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )({
        ...recipeData,
        playerNumber: recipeData["Number for Monogramming"],
        playerName: recipeData["Name for Monogramming"],
        size_left: recipeData["Left Length"],
        size_right: recipeData["Right Length"],
        SkateCustomiseBlade: skateCustomiseBlade,
        customizeBladePlayerName: recipeData["Blade Name"],
        customizeBladePlayerNumber: recipeData["Blade Number"],
        customizeBladeNoPersonalization:
          has("Blade Number")(recipeData) || has("Blade Name")(recipeData)
            ? false
            : has("customizeBladeNoPersonalization")(initialUserChoiceValues)
            ? initialUserChoiceValues.customizeBladeNoPersonalization
            : true,
        noPersonalization:
          has("Number for Monogramming")(recipeData) ||
          has("Name for Monogramming")(recipeData)
            ? false
            : has("noPersonalization")(initialUserChoiceValues)
            ? initialUserChoiceValues.noPersonalization
            : true,

        ...customiseBladeValues,
      });

    case "CustomSteel2023":
      const steelCustomiseBlade =
        recipeData["Blade Name"] ||
        recipeData["Blade Number"] ||
        recipeData["Performance Radius"] ||
        recipeData["Sharpening"]
          ? true
          : false;

      const steelCustomiseBladeValues = steelCustomiseBlade
        ? getSkateCustomiseBladeValuesFromRecipe(recipeData)(
            productCustomisations
          )
        : {};

      return flow(
        omit([]),
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )({
        ...recipeData,
        SkateCustomiseBlade: steelCustomiseBlade,
        customizeBladePlayerName: recipeData["Blade Name"],
        customizeBladePlayerNumber: recipeData["Blade Number"],
        customizeBladeNoPersonalization:
          has("Blade Number")(recipeData) || has("Blade Name")(recipeData)
            ? false
            : has("customizeBladeNoPersonalization")(initialUserChoiceValues)
            ? initialUserChoiceValues.customizeBladeNoPersonalization
            : true,
        ...steelCustomiseBladeValues,
      });

    case "ProCustomPlayerStick2023":
      return flow(
        omit([
          "Name Bar ('Nick Name')",
          "Personalization (player name)",
          'End Plug (aka 4" extension)',
        ]),
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )({
        ...recipeData,
        playerName: recipeData["Name Bar ('Nick Name')"],
        addExtension: has('End Plug (aka 4" extension)')(recipeData)
          ? recipeData['End Plug (aka 4" extension)'] !== "Stock"
          : null,
        noPersonalization:
          !has("Name Bar ('Nick Name')")(recipeData) &&
          !has("noPersonalization")(recipeData)
            ? null
            : has("Name Bar ('Nick Name')")(recipeData)
            ? false
            : true,
      });

    case "HyperlitePlayerStick2023":
    case "FlylitePlayerStick2025":
    case "TracerPlayerStick2024":
    case "SupremePlayerStick2020":
    case "TWITCHPlayerStick2024":
    case "PulsePlayerStick2025":
    case "CustomPROTOR":
    case "PROTO2PlayerStick2025":
      return flow(
        omit(["Name Bar ('Nick Name')", 'End Plug (aka 4" extension)']),
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )({
        ...recipeData,
        playerName: recipeData["Name Bar ('Nick Name')"],
        ...(recipeData['End Plug (aka 4" extension)']
          ? {
              addExtension: recipeData['End Plug (aka 4" extension)'] === "Yes",
            }
          : {}),
        noPersonalization: has("Name Bar ('Nick Name')")(recipeData)
          ? false
          : has("noPersonalization")(initialUserChoiceValues)
          ? initialUserChoiceValues.noPersonalization
          : true,
      });

    case "QuickTurnVaporStick":
    case "QuickTurnVaporFlylitePlayerStick":
    case "QuickTurnNexusStick":
    case "QuickTurnTwitchStick":
    case "QuickTurnPulsePlayerStick":
    case "QuickTurnSupremeStick":
    case "QuickTurnProtoR":
    case "QuickTurnProto2PlayerStick":
      return flow(
        omit([
          "Hand (aka curve or handedness)",
          'End Plug (aka 4" extension)',
          "Name Bar ('Nick Name')",
          "Color (or country) text",
        ]),
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )({
        ...recipeData,
        Hand: recipeData["Hand (aka curve or handedness)"],
        ...(recipeData['End Plug (aka 4" extension)']
          ? {
              addExtension: recipeData['End Plug (aka 4" extension)'] === "Yes",
            }
          : {}),
        playerName: recipeData["Name Bar ('Nick Name')"],
        Color: recipeData["Color (or country) text"],
        noPersonalization: has("Name Bar ('Nick Name')")(recipeData)
          ? false
          : has("noPersonalization")(initialUserChoiceValues)
          ? initialUserChoiceValues.noPersonalization
          : true,
      });

    case "ProCustomGoalierGear2023":
    case "DigiPrintGoalieGear2024":
    case "VaporHyperliteGoalierGear2023":
    case "VaporFlyliteGoalieGear2025":
    case "VaporFlyliteGoalieGear2026":
    case "SVProGoalieGear2026":
    case "SupremeMachGoalierGear2023":
    case "SupremeShadowGoalieGear2024":
    case "SupremeFuseGoalieGear2026":
      return flow(
        getOr("{}", "webAppValues"),
        JSON.parse,
        omitBy(isNil),
        merge(initialUserChoiceValues)
      )(recipeData);

    default:
      return recipeData;
  }
}
